import React from "react";
import TextInput from "../../../common/input/TextInput";
import TextAreaInput from "../../../common/input/TextAreaInput";
import RadioInput from "../../../common/input/RadioInput";
import PhoneNumberInput from "../../../common/input/PhoneNumberInput";
import PasswordInput from "../../../common/input/PasswordInput";
import CheckBoxInput from "../../../common/input/CheckboxInput";
import NumberInput from "@/common/input/NumberInput";
import SelectBox from "@/common/input/SelectBox";
import DependentSelectBox from "@/common/input/DependentSelectBox";
import CountrySelectInput from "@/common/input/CountrySelectInput";
import DateInput from "@/common/input/DateInput";
import { AddressInputWithModal } from "@/common/input/AddressInput";
import UploadFileInput from "@/common/input/UploadFileInput";
import UploadDocumentsInput from "@/common/input/UploadDocumentsInput";
import SelectBoxFromApi from "@/common/input/SelectBoxFromApi";
import NumberWithCurrencyInput from "@/common/input/NumberWithCurrencyInput";
import DateRange from "@/common/input/DateRange";
import DependentStateSelectInput from "@/common/input/DependentSelectStateInput";
import DependentSelectCityInput from "@/common/input/DependentSelectCityInput";
import SocialInput from "@/common/input/SocialInput";

interface RenderWidgetsProps {
  widget: any;
  defaultValue?: any;
  optionsFromAPI?: any;
  forceDisabled?: boolean;
  loopData?: any;
}

const RenderWidgets: React.FC<RenderWidgetsProps> = ({ widget, forceDisabled = false, defaultValue, optionsFromAPI, loopData }) => {
  const { widgetType, label, field, placeholder } = widget;

  const getWidgetsComponent = (widgetType: any) => {
    switch (widgetType) {
      case "text":
        return (
          <TextInput
            label={label}
            field={field}
            placeholder={placeholder}
            validation={widget.validation}
            disabled={widget?.isDisabled || forceDisabled}
            defaultValue={defaultValue}
            setValueOnDisabled={widget?.setValueOnDisabled}
          />
        );
      case "number":
        return (
          <NumberInput
            label={label}
            field={field}
            placeholder={placeholder}
            validation={widget.validation}
            disabled={widget?.isDisabled || forceDisabled}
            defaultValue={defaultValue}
            unit={widget?.unit}
          />
        );
      case "date":
        return (
          <DateInput
            label={label}
            field={field}
            placeholder={placeholder}
            validation={widget.validation}
            disabled={widget?.isDisabled || forceDisabled}
            defaultValue={defaultValue}
          />
        );
      case "password":
        return (
          <PasswordInput label={label} field={field} placeholder={placeholder} validation={widget.validation} disabled={widget?.isDisabled || forceDisabled} />
        );
      case "select":
        return (
          <SelectBox
            label={label}
            field={field}
            placeholder={placeholder}
            options={widget?.options || optionsFromAPI}
            validation={widget.validation}
            disabled={widget?.isDisabled || forceDisabled}
            showSearch={widget?.showSearch}
            defaultValue={defaultValue}
            isAddOtherEnabled={widget?.isAddOtherEnabled}
            formValueKey={widget?.formValueKey}
          />
        );
      case "select-country":
        return (
          <CountrySelectInput
            label={label}
            field={field}
            placeholder={placeholder}
            validation={widget.validation}
            disabled={widget?.isDisabled || forceDisabled}
            showSearch={widget?.showSearch}
            defaultValue={defaultValue}
          />
        );
      case "select-state":
        return (
          <DependentStateSelectInput
            label={label}
            field={field}
            placeholder={placeholder}
            validation={widget.validation}
            disabled={widget?.isDisabled || forceDisabled}
            showSearch={widget?.showSearch}
            defaultValue={defaultValue}
            dependentFieldName={widget?.dependentFieldName}
          />
        );
      case "select-city":
        return (
          <DependentSelectCityInput
            label={label}
            field={field}
            placeholder={placeholder}
            validation={widget.validation}
            disabled={widget?.isDisabled || forceDisabled}
            showSearch={widget?.showSearch}
            defaultValue={defaultValue}
            countryFieldName={widget?.countryFieldName}
            stateFieldName={widget?.stateFieldName}
          />
        );
      case "dependent-select":
        return (
          <DependentSelectBox
            label={label}
            field={field}
            placeholder={placeholder}
            options={widget?.options}
            validation={widget.validation}
            disabled={widget?.isDisabled || forceDisabled}
            showSearch={widget?.showSearch}
            optionKeyField={widget?.optionKeyField}
            defaultValue={defaultValue}
            messageOnNoOption={widget?.messageOnNoOption}
          />
        );
      case "select-from-api":
        return (
          <SelectBoxFromApi
            label={label}
            field={field}
            placeholder={placeholder}
            optionsApiUrl={widget.optionsApiUrl}
            validation={widget.validation}
            disabled={widget?.isDisabled || forceDisabled}
            showSearch={widget?.showSearch}
            defaultValue={defaultValue}
            addNewOptions={widget?.addNewOptions}
            isAddEmpIDFilter={widget?.isAddEmpIDFilter}
            isObjectAsValue={widget?.isObjectAsValue}
            updateOptions={widget?.updateOptions}
          />
        );
      case "textarea":
        return (
          <TextAreaInput
            placeholder={placeholder}
            field={field}
            label={label}
            rows={widget.rows}
            columns={widget.columns}
            validation={widget.validation}
            defaultValue={widget?.defaultValue || defaultValue}
          />
        );
      case "radio":
        return (
          <RadioInput
            field={field}
            label={label}
            required={widget.validation?.required}
            options={widget?.options}
            defaultValue={widget?.defaultValue || defaultValue}
          />
        );
      case "phone":
        return (
          <PhoneNumberInput
            field={field}
            label={label}
            placeholder={placeholder}
            disabled={widget.isDisabled}
            validation={widget.validation}
            defaultCountry={defaultValue || widget?.defaultValue || widget?.defaultCountry}
            countryCodeList={widget?.countryCodeList}
            newFill={widget?.newFill}
            infoText={widget?.infoText}
          />
        );
      case "number-with-currency":
        return (
          <NumberWithCurrencyInput
            field={field}
            label={label}
            placeholder={placeholder}
            disabled={widget.isDisabled}
            validation={widget.validation}
            defaultCountry={defaultValue || widget?.defaultValue}
            countryCodeList={widget?.countryCodeList}
            currencySelectDisabled={widget?.currencySelectDisabled}
            autoFillDependentFieldValue={widget?.autoFillDependentFieldValue}
          />
        );
      case "checkbox":
        return (
          <CheckBoxInput label={label} options={widget.options} field={field} validation={widget.validation} disabled={widget?.isDisabled || forceDisabled} />
        );
      case "address":
        return (
          <AddressInputWithModal
            placeholder={placeholder}
            field={field}
            label={label}
            rows={widget.rows}
            columns={widget.columns}
            validation={widget.validation}
            defaultValue={defaultValue}
          />
        );

      case "upload-single-file":
        return (
          <UploadFileInput
            placeholder={placeholder}
            field={field}
            label={label}
            message={widget?.message}
            validation={widget.validation}
            defaultValue={defaultValue}
            uploadKey={widget.uploadKey}
            isFreeCrop={widget?.isFreeCrop}
          />
        );
      case "upload-document-file":
        return (
          <UploadDocumentsInput
            placeholder={placeholder}
            field={field}
            label={label}
            wigetInfo={widget?.wigetInfo}
            validation={widget.validation}
            defaultValue={defaultValue}
            uploadKey={widget.uploadKey}
            previewInfo={loopData}
          />
        );
      case "dateRange":
        return (
          <DateRange
            label={label}
            field={field}
            fields={widget?.fields}
            placeholder={placeholder}
            validation={widget.validation}
            disabled={widget?.isDisabled || forceDisabled}
            restrictFutureDates={widget?.restrictFutureDates}
            defaultValue={defaultValue}
          />
        );
      case "social":
        return (
          <SocialInput
            field={field}
            fields={widget?.fields}
            validation={widget.validation}
            disabled={widget?.isDisabled || forceDisabled}
            defaultValue={defaultValue}
          />
        );
    }
  };
  return <>{getWidgetsComponent(widgetType)}</>;
};

export default RenderWidgets;
